import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Mail, Eye, EyeOff, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_immobilier-nantes/artifacts/rfdvblj1_Logo%20Inter%27Med%20Immobilier.png";

const AdminLogin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [registerData, setRegisterData] = useState({ email: "", password: "", nom: "" });
  const [errors, setErrors] = useState({});

  const handleLogin = async (e) => {
    e.preventDefault();
    setErrors({});
    
    if (!loginData.email || !loginData.password) {
      setErrors({ login: "Veuillez remplir tous les champs" });
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, loginData);
      localStorage.setItem("admin_token", response.data.token);
      localStorage.setItem("admin_user", JSON.stringify(response.data.admin));
      toast.success("Connexion réussie !");
      navigate("/admin/dashboard");
    } catch (error) {
      console.error("Login error:", error);
      const message = error.response?.data?.detail || "Erreur de connexion";
      setErrors({ login: message });
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setErrors({});
    
    if (!registerData.email || !registerData.password || !registerData.nom) {
      setErrors({ register: "Veuillez remplir tous les champs" });
      return;
    }

    if (registerData.password.length < 6) {
      setErrors({ register: "Le mot de passe doit contenir au moins 6 caractères" });
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/register`, registerData);
      localStorage.setItem("admin_token", response.data.token);
      localStorage.setItem("admin_user", JSON.stringify(response.data.admin));
      toast.success("Compte créé avec succès !");
      navigate("/admin/dashboard");
    } catch (error) {
      console.error("Register error:", error);
      const message = error.response?.data?.detail || "Erreur lors de l'inscription";
      setErrors({ register: message });
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper flex items-center justify-center p-4" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="Inter'med Immobilier" className="h-16 mx-auto mb-4" />
          <h1 className="font-heading text-2xl text-harbor">Espace Administration</h1>
          <p className="text-muted-foreground text-sm">Gestion des leads et prospects</p>
        </div>

        <Card className="shadow-lg" data-testid="admin-auth-card">
          <CardContent className="p-6">
            <Tabs defaultValue="login" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6" data-testid="auth-tabs">
                <TabsTrigger value="login" data-testid="tab-login">Connexion</TabsTrigger>
                <TabsTrigger value="register" data-testid="tab-register">Inscription</TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
                  <div>
                    <Label htmlFor="login-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                      <Input
                        id="login-email"
                        type="email"
                        value={loginData.email}
                        onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                        className="pl-10"
                        placeholder="admin@intermed-immobilier.com"
                        data-testid="login-email-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="login-password">Mot de passe</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                      <Input
                        id="login-password"
                        type={showPassword ? "text" : "password"}
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        className="pl-10 pr-10"
                        placeholder="••••••••"
                        data-testid="login-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  {errors.login && (
                    <p className="text-red-500 text-sm" data-testid="login-error">{errors.login}</p>
                  )}

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-harbor hover:bg-harbor/90 text-white"
                    data-testid="login-submit"
                  >
                    {loading ? "Connexion..." : "Se connecter"}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4" data-testid="register-form">
                  <div>
                    <Label htmlFor="register-nom">Nom complet</Label>
                    <div className="relative">
                      <UserPlus className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                      <Input
                        id="register-nom"
                        type="text"
                        value={registerData.nom}
                        onChange={(e) => setRegisterData({ ...registerData, nom: e.target.value })}
                        className="pl-10"
                        placeholder="Jean Dupont"
                        data-testid="register-nom-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="register-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                      <Input
                        id="register-email"
                        type="email"
                        value={registerData.email}
                        onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                        className="pl-10"
                        placeholder="admin@intermed-immobilier.com"
                        data-testid="register-email-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="register-password">Mot de passe</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                      <Input
                        id="register-password"
                        type={showPassword ? "text" : "password"}
                        value={registerData.password}
                        onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                        className="pl-10 pr-10"
                        placeholder="••••••••"
                        data-testid="register-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  {errors.register && (
                    <p className="text-red-500 text-sm" data-testid="register-error">{errors.register}</p>
                  )}

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-growth hover:bg-growth/90 text-white"
                    data-testid="register-submit"
                  >
                    {loading ? "Création..." : "Créer un compte"}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-muted-foreground mt-6">
          <a href="/" className="hover:text-harbor transition-colors">
            ← Retour au site
          </a>
        </p>
      </div>
    </div>
  );
};

export default AdminLogin;
